import { useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, Shield, Check, X, AlertTriangle, Search,
  Loader2, Globe, Mail, Lock, ChevronDown, ChevronUp, Info,
} from "lucide-react";

import { API_URL } from "@/lib/siteConfig";
const API_BASE = API_URL;

interface DnsCheck {
  name: string;
  status: "pass" | "warn" | "fail" | "missing";
  value: string | null;
  details: string;
}

interface CheckResult {
  domain: string;
  grade: string;
  gradeColor: string;
  checks: DnsCheck[];
  summary: { total: number; pass: number; warn: number; fail: number };
}

const STATUS_ICON: Record<string, { icon: any; color: string; bg: string }> = {
  pass: { icon: Check, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  warn: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  fail: { icon: X, color: "text-red-500", bg: "bg-red-50 border-red-200" },
  missing: { icon: X, color: "text-red-500", bg: "bg-red-50 border-red-200" },
};

const GRADE_STYLE: Record<string, string> = {
  green: "from-emerald-500 to-emerald-600 text-white",
  amber: "from-amber-400 to-amber-500 text-white",
  red: "from-red-500 to-red-600 text-white",
};

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "DMARC SPF DKIM Checker",
  applicationCategory: "BusinessApplication",
  description: "Free email authentication checker. Verify SPF, DKIM, DMARC, and MX records for any domain. Get an instant grade and plain-English fix recommendations.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  operatingSystem: "Web",
};

export default function DmarcCheckerPage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedCheck, setExpandedCheck] = useState<number | null>(null);
  const [showGate, setShowGate] = useState(false);
  const [pendingResult, setPendingResult] = useState<CheckResult | null>(null);

  async function handleCheck() {
    const cleaned = domain.trim().toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "")
      .replace(/^www\./, "");

    if (!cleaned) {
      setError("Please enter a domain name.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setExpandedCheck(null);

    try {
      const res = await fetch(`${API_BASE}/tools/dns-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: cleaned }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Check failed. Please try again.");
        return;
      }
      // Gate: show email modal before revealing results
      setPendingResult(data);
      setShowGate(true);
    } catch {
      setError("Failed to connect. Please check your internet and try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleGateComplete() {
    setShowGate(false);
    if (pendingResult) {
      setResult(pendingResult);
      const firstIssue = pendingResult.checks.findIndex((c: DnsCheck) => c.status !== "pass");
      setExpandedCheck(firstIssue >= 0 ? firstIssue : 0);
      setPendingResult(null);
    }
  }

  return (
    <>
      {showGate && (
        <EmailGateModal toolName="domain authentication report" source="/dmarc-checker"
          onComplete={handleGateComplete} onClose={() => { setShowGate(false); handleGateComplete(); }} />
      )}
      <SeoHead
        title="Free DMARC, SPF & DKIM Checker — Email Authentication Lookup Tool | MoreMail"
        description="Check any domain's email authentication in seconds. Free tool verifies SPF, DKIM, DMARC, and MX records with an instant grade and plain-English fix recommendations."
        canonical={`${SITE_URL}/dmarc-checker`}
        keywords="DMARC checker, SPF checker, DKIM checker, email authentication checker, DMARC lookup, SPF lookup, DKIM lookup, email security checker, domain email check, MX record checker, DMARC validator, SPF validator"
        ogTitle="Free DMARC, SPF & DKIM Checker — Instant Email Auth Grade | MoreMail"
        ogDescription="Enter any domain. Get an instant A–F grade for email authentication. Check SPF, DKIM, DMARC, and MX records with fix recommendations."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-12 pb-6 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 backdrop-blur-sm">
            <Shield size={13} className="text-coral" /> Free Tool
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            DMARC, SPF & DKIM <span className="text-coral">Checker</span>
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto mb-8">
            Check if your domain's email authentication is properly configured.
            Get an instant grade with plain-English recommendations.
          </p>

          {/* Input */}
          <div className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCheck()}
                  placeholder="Enter a domain (e.g. example.com)"
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-coral focus:ring-2 focus:ring-coral/30 transition-all backdrop-blur-sm"
                  disabled={loading}
                />
              </div>
              <button
                onClick={handleCheck}
                disabled={loading || !domain.trim()}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-coral text-white text-sm font-bold hover:bg-coral-dark transition-colors shadow-lg shadow-coral/20 disabled:opacity-50 flex-shrink-0"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                {loading ? "Checking..." : "Check Domain"}
              </button>
            </div>
            {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
          </div>
        </div>
      </section>

      {/* Results */}
      {result && (
        <section className="py-10 bg-paper">
          <div className="max-w-3xl mx-auto px-6">

            {/* Grade card */}
            <div className="bg-white rounded-xl border border-sand p-6 mb-6 flex flex-col sm:flex-row items-center gap-6">
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${GRADE_STYLE[result.gradeColor] || GRADE_STYLE.amber} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                <span className="font-display font-bold text-4xl">{result.grade}</span>
              </div>
              <div className="text-center sm:text-left">
                <h2 className="font-display font-bold text-ink text-xl">{result.domain}</h2>
                <p className="text-sm text-slate mt-1">
                  {result.summary.pass} passed · {result.summary.warn} warning{result.summary.warn !== 1 ? "s" : ""} · {result.summary.fail} failed
                </p>
                <p className="text-xs text-slate/60 mt-1">
                  {result.grade === "A" ? "Excellent email authentication! Your domain is well-protected." :
                   result.grade === "B" ? "Good setup with minor improvements possible." :
                   result.grade === "C" ? "Partial protection. Some records need attention." :
                   result.grade === "D" ? "Significant gaps in email authentication." :
                   "Critical issues found. Your domain is vulnerable to email spoofing."}
                </p>
              </div>
            </div>

            {/* Individual checks */}
            <div className="space-y-3">
              {result.checks.map((check, i) => {
                const statusInfo = STATUS_ICON[check.status] || STATUS_ICON.fail;
                const Icon = statusInfo.icon;
                const isExpanded = expandedCheck === i;

                return (
                  <div key={check.name} className={`rounded-xl border overflow-hidden ${statusInfo.bg}`}>
                    <button
                      onClick={() => setExpandedCheck(isExpanded ? null : i)}
                      className="w-full flex items-center gap-3 p-4 text-left"
                    >
                      <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0`}>
                        <Icon size={16} className={statusInfo.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-ink">{check.name}</span>
                          <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                            check.status === "pass" ? "bg-emerald-100 text-emerald-700" :
                            check.status === "warn" ? "bg-amber-100 text-amber-700" :
                            "bg-red-100 text-red-600"
                          }`}>
                            {check.status === "pass" ? "Pass" : check.status === "warn" ? "Warning" : "Fail"}
                          </span>
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp size={16} className="text-slate" /> : <ChevronDown size={16} className="text-slate" />}
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4">
                        <div className="bg-white rounded-lg p-4 border border-sand/50">
                          <p className="text-sm text-slate leading-relaxed mb-3">{check.details}</p>
                          {check.value && (
                            <div className="bg-paper rounded-lg p-3 mt-2">
                              <p className="text-[10px] font-semibold text-slate mb-1 uppercase tracking-wide">Record Value</p>
                              <p className="text-xs text-ink font-mono break-all leading-relaxed">{check.value}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Check another */}
            <div className="mt-6 text-center">
              <button onClick={() => { setResult(null); setDomain(""); }}
                className="text-sm text-coral font-semibold hover:underline">
                ← Check another domain
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Explainer section (shows when no results) */}
      {!result && (
        <section className="py-12 bg-paper">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
              What We <span className="text-coral">Check</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: "SPF (Sender Policy Framework)", desc: "SPF tells receiving servers which IP addresses are allowed to send email on behalf of your domain. Without it, anyone can send email pretending to be you.", icon: Shield },
                { name: "DKIM (DomainKeys Identified Mail)", desc: "DKIM adds a digital signature to your emails that proves they haven't been tampered with in transit. It's like a wax seal for your emails.", icon: Lock },
                { name: "DMARC (Domain-based Message Authentication)", desc: "DMARC tells receiving servers what to do when SPF or DKIM checks fail — reject, quarantine, or allow. It also enables reporting so you can monitor abuse.", icon: Mail },
                { name: "MX Records", desc: "MX records tell the internet where to deliver email for your domain. Without them, nobody can send you email. We check that at least one is properly configured.", icon: Globe },
              ].map(item => (
                <div key={item.name} className="bg-white rounded-xl p-5 border border-sand">
                  <item.icon size={20} className="text-coral mb-3" />
                  <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.name}</h3>
                  <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why it matters */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-3">
            Why Email Authentication <span className="text-coral">Matters</span>
          </h2>
          <p className="text-sm text-slate text-center mb-8 max-w-lg mx-auto">
            Properly configured email authentication improves deliverability, protects your brand, and prevents spoofing.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Better Deliverability", desc: "Emails from authenticated domains are more likely to reach the inbox instead of spam. Gmail, Outlook, and Yahoo all check these records.", pct: "10-20%", pctLabel: "higher inbox rate" },
              { title: "Brand Protection", desc: "Without DMARC, anyone can send emails that look like they're from your domain. Phishing attacks can destroy customer trust overnight.", pct: "3.4B", pctLabel: "spoofed emails daily" },
              { title: "Compliance Ready", desc: "Google and Yahoo now require SPF, DKIM, and DMARC for bulk senders. If you send more than 5,000 emails/day, these are mandatory.", pct: "2024", pctLabel: "requirement deadline" },
            ].map(item => (
              <div key={item.title} className="bg-paper rounded-xl p-5 border border-sand text-center">
                <p className="font-display font-bold text-coral text-2xl">{item.pct}</p>
                <p className="text-[10px] text-slate mb-3">{item.pctLabel}</p>
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.title}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Authentication Is Just the Start
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            MoreMail monitors your deliverability, checks blacklists, warms up your domain,
            and gives you tools to ensure every email reaches the inbox.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
